/*
 * @providesModule ForgotPasswordView
 */

import React, {useState, useRef, useEffect} from 'react';
import {View, Animated, Dimensions, Keyboard, Platform} from 'react-native';
import ForgotPasswordForm from 'ForgotPasswordForm';
import styles, {MODAL_HEIGHT, MODAL_ERROR_HEIGHT} from 'ForgotPasswordStyle';

interface ForgotPasswordProps {
  //handle cancel action
  onCancelPress: () => void;
  //handle submit
  onSubmitPress: (username: string) => Promise<boolean>;
  //callback on submit response
  submitResponseCallback: (email: string) => void;
}

const ForgotPasswordView = (props: ForgotPasswordProps) => {
  const [email, setEmail] = useState<string>('');
  //True if username/email input is focus on for styling
  const [inputUserNameFocus, setUserNameFocus] = useState<boolean>(false);
  const [inputError, setInputError] = useState<boolean>(false);
  const [isKeyboardUp, setIsKeyboardUp] = useState<boolean>(false);
  const [screenHeight, setScreenHeight] = useState(Dimensions.get('window').height);
  const [viewHeight, setViewHeight] = useState<number>(MODAL_HEIGHT);
  const heightMinusView = screenHeight - viewHeight;
  const [keyboardPosition, setKeyboardPosition] = useState<number>(heightMinusView);
  const panY = useRef(new Animated.Value(screenHeight)).current;
  const keyboardOffset = useRef(new Animated.Value(heightMinusView)).current;
  const translateY = panY.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: [1, 0, 1],
  });

  useEffect(() => {
    const updatePosition = () => {
      const newScreenHeight = Dimensions.get('window').height;
      setScreenHeight(newScreenHeight);
      const newHeightMinusView = newScreenHeight - viewHeight;
      setKeyboardPosition(newHeightMinusView);

      panY.setValue(newHeightMinusView);
      keyboardOffset.setValue(newHeightMinusView);
    };

    const dimensionsSubscription = Dimensions.addEventListener('change', updatePosition);

    return () => {
      dimensionsSubscription.remove();
    };
  }, [viewHeight]);

  const animatedKeyboard = (keyboardHeight: number, keyboardVisible: boolean) => {
    let androidMultiplier = 1;
    if (-keyboardHeight < 249) {
      androidMultiplier = 1.2;
    }
    const currentHeightMinusView = screenHeight - viewHeight;
    const iosOffset = inputError
      ? currentHeightMinusView + keyboardHeight / 3.5 - 10
      : currentHeightMinusView + keyboardHeight / 3.5;
    const androidOffset = currentHeightMinusView + keyboardHeight * androidMultiplier;
    const offset = Platform.OS == 'ios' ? iosOffset : androidOffset;
    const value = keyboardVisible ? offset : keyboardHeight;
    Animated.timing(keyboardOffset, {
      toValue: value,
      duration: 200,
      useNativeDriver: false,
    }).start();
  };

  const resetPositionAnim = Animated.timing(panY, {
    toValue: heightMinusView,
    duration: 400,
    useNativeDriver: true,
  });

  const closeAnim = Animated.timing(keyboardOffset, {
    toValue: screenHeight,
    duration: 500,
    useNativeDriver: true,
  });

  useEffect(() => {
    resetPositionAnim.start(() => {
      setIsKeyboardUp(true);
    });
  }, [resetPositionAnim]);

  useEffect(() => {
    const showListener = Platform.OS == 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideListener = Platform.OS == 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    let keyboardListenercalled = false;

    const showSubscription = Keyboard.addListener(showListener, (e) => {
      setKeyboardPosition(-e.endCoordinates?.height);
      animatedKeyboard(-e.endCoordinates?.height, true);
      keyboardListenercalled = true;
    });

    const hideSubscription = Keyboard.addListener(hideListener, (e) => {
      const newHeightMinusView = screenHeight - viewHeight;
      setKeyboardPosition(newHeightMinusView);
      animatedKeyboard(newHeightMinusView, false);
      keyboardListenercalled = true;
    });

    if (!keyboardListenercalled) {
      if (keyboardPosition == heightMinusView || keyboardPosition == heightMinusView + 14) {
        setKeyboardPosition(heightMinusView);
        animatedKeyboard(heightMinusView, false);
      } else {
        animatedKeyboard(keyboardPosition, true);
      }
    }
    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [inputError, screenHeight, viewHeight]);

  const onChangeText = (text: string) => {
    setInputError(false);
    setEmail(text);
  };

  const handleSubmitPress = async () => {
    Keyboard.dismiss();
    if (email.length) {
      try {
        const success = await props.onSubmitPress(email);
        if (!success) {
          setViewHeight(MODAL_ERROR_HEIGHT);
          setInputError(true);
        } else {
          closeAnim.start(() => {
            props.submitResponseCallback(email);
          });
        }
      } catch (error) {
        console.warn('Error in submit press', error);
      }
    }
  };

  const handleCancelPress = () => {
    closeAnim.start(() => {
      props.onCancelPress();
    });
  };

  return (
    <View style={styles.containerOverlay}>
      <Animated.View
        style={{
          transform: [{translateY: isKeyboardUp ? keyboardOffset : translateY}],
        }}
      >
        <ForgotPasswordForm
          email={email}
          onChangeText={onChangeText}
          onSubmitPress={handleSubmitPress}
          onCancelPress={handleCancelPress}
          inputUserNameFocus={inputUserNameFocus}
          setUserNameFocus={setUserNameFocus}
          inputError={inputError}
          viewHeight={viewHeight}
        />
      </Animated.View>
    </View>
  );
};

export default ForgotPasswordView;
